import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface TourActionRequest {
  addonCode: string
  action: string
}

export interface TourActionResult {
  addonCode: string
  data: Record<string, unknown>
}

/**
 * Manages restart signals and inter-component action coordination for CP add-on tours.
 *
 * Flow for view-triggered actions (e.g. auto-creating a demo resource):
 *   1. Tour component calls requestTourAction() to signal the view.
 *   2. The view handles the action and calls confirmTourAction() with result data.
 *   3. Tour component watches actionResult and continues the tour accordingly.
 */
export const useCpAddonToursStore = defineStore('cpAddonTours', () => {
  /** Incremented to request a tour restart for a specific add-on code. */
  const restartSignals = ref<Record<string, number>>({})

  /**
   * Set by the tour component to request an action from the active view.
   * The view watches this and executes the action, then calls confirmTourAction().
   */
  const pendingAction = ref<TourActionRequest | null>(null)

  /**
   * Set by the view after completing a pending action.
   * The tour component watches this to resume the tour with the result data.
   */
  const actionResult = ref<TourActionResult | null>(null)

  function requestTourRestart(addonCode: string): void {
    restartSignals.value = {
      ...restartSignals.value,
      [addonCode]: (restartSignals.value[addonCode] ?? 0) + 1,
    }
  }

  function requestTourAction(addonCode: string, action: string): void {
    actionResult.value = null
    pendingAction.value = { addonCode, action }
  }

  function confirmTourAction(addonCode: string, data: Record<string, unknown>): void {
    pendingAction.value = null
    actionResult.value = { addonCode, data }
  }

  return { restartSignals, pendingAction, actionResult, requestTourRestart, requestTourAction, confirmTourAction }
})
